import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, ShieldCheck, Database, Filter } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../config/supabase';

type RLSRow = Record<string, unknown>;
type RlsTab =
  | 'pilot'
  | 'financialProfiles'
  | 'packages'
  | 'clientReportSettings'
  | 'assessmentSessionsV2'
  | 'assessmentResultsV2';
type PackageTable = 'packages' | 'package_session_usage' | 'package_credit_additions';
type ContentTable = 'events' | 'courses' | 'newspage' | 'courseleads';

const LIMIT_OPTIONS = [20, 50, 100] as const;

const PACKAGE_TABLE_OPTIONS: Array<{ value: PackageTable; label: string }> = [
  { value: 'packages', label: 'packages' },
  { value: 'package_session_usage', label: 'package_session_usage' },
  { value: 'package_credit_additions', label: 'package_credit_additions' },
];

const PACKAGE_TABLE_ORDER_FIELD: Record<PackageTable, string> = {
  packages: 'packageCreatedAt',
  package_session_usage: 'usageDate',
  package_credit_additions: 'additionDate',
};

const CONTENT_TABLE_OPTIONS: Array<{ value: ContentTable; label: string }> = [
  { value: 'events', label: 'events' },
  { value: 'courses', label: 'courses' },
  { value: 'newspage', label: 'newspage' },
  { value: 'courseleads', label: 'courseleads' },
];

const CONTENT_TABLE_ORDER_FIELD: Record<ContentTable, string> = {
  events: 'eventCreatedAt',
  courses: 'courseCreatedAt',
  newspage: 'newspagePublishDate',
  courseleads: 'courseLeadsCreatedAt',
};

const CONTENT_TABLE_ID_FIELD: Record<ContentTable, string> = {
  events: 'eventId',
  courses: 'courseId',
  newspage: 'newspageId',
  courseleads: 'courseLeadsId',
};

const CONTENT_TABLE_STATUS_FIELD: Partial<Record<ContentTable, string>> = {
  events: 'eventStatus',
  courses: 'courseStatus',
  courseleads: 'courseLeadsStatus',
};

export default function RLSTestPage() {
  const { user, selectedClient } = useAuth();

  const [activeTab, setActiveTab] = useState<RlsTab>('pilot');
  const [packageTable, setPackageTable] = useState<PackageTable>('packages');
  const [contentTable, setContentTable] = useState<ContentTable>('events');
  const [clientIdFilter, setClientIdFilter] = useState<string>(selectedClient?.clientId ?? '');
  const [patientIdFilter, setPatientIdFilter] = useState<string>('');
  const [packageIdFilter, setPackageIdFilter] = useState<string>('');
  const [contentRecordIdFilter, setContentRecordIdFilter] = useState<string>('');
  const [contentStatusFilter, setContentStatusFilter] = useState<string>('');
  const [financialProfileIdFilter, setFinancialProfileIdFilter] = useState<string>('');
  const [appointmentIdFilter, setAppointmentIdFilter] = useState<string>('');
  const [invoiceIdFilter, setInvoiceIdFilter] = useState<string>('');
  const [sessionIdFilter, setSessionIdFilter] = useState<string>('');
  const [testKeyFilter, setTestKeyFilter] = useState<string>('');
  const [statusValueFilter, setStatusValueFilter] = useState<string>('');
  const [limitValue, setLimitValue] = useState<number>(50);
  const [rows, setRows] = useState<RLSRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<string | null>(null);
  const [allowedIssuer, setAllowedIssuer] = useState<boolean | null>(null);
  const [selectedClientAccess, setSelectedClientAccess] = useState<boolean | null>(null);
  const [rlsProbeLoading, setRlsProbeLoading] = useState<boolean>(false);
  const [rlsProbeError, setRlsProbeError] = useState<string | null>(null);

  const tableName = useMemo(() => {
    if (activeTab === 'pilot') return 'rls_pilot_events';
    if (activeTab === 'financialProfiles') return 'financial_profiles';
    if (activeTab === 'clientReportSettings') return 'client_report_settings';
    if (activeTab === 'assessmentSessionsV2') return 'assessment_sessions_v2';
    if (activeTab === 'assessmentResultsV2') return 'assessment_results_v2';
    return packageTable;
  }, [activeTab, contentTable, packageTable]);

  const orderField = useMemo(() => {
    if (activeTab === 'pilot') return 'createdAt';
    if (activeTab === 'financialProfiles') return 'financialProfileCreatedAt';
    if (activeTab === 'clientReportSettings') return 'updatedAt';
    if (activeTab === 'assessmentSessionsV2') return 'sessionDate';
    if (activeTab === 'assessmentResultsV2') return 'assessmentDate';
    return PACKAGE_TABLE_ORDER_FIELD[packageTable];
  }, [activeTab, contentTable, packageTable]);

  const columns = useMemo(() => {
    const keys = new Set<string>();
    for (const row of rows) {
      Object.keys(row).forEach((key) => keys.add(key));
    }
    return Array.from(keys);
  }, [rows]);

  const isPackagesTab = activeTab === 'packages';
  const isFinancialProfilesTab = activeTab === 'financialProfiles';
  const isAssessmentSessionsTab = activeTab === 'assessmentSessionsV2';
  const isAssessmentResultsTab = activeTab === 'assessmentResultsV2';
  const isPackagesMainTable = isPackagesTab && packageTable === 'packages';
  const isPackagesUsageTable = isPackagesTab && packageTable === 'package_session_usage';
  const isPackagesCreditsTable = isPackagesTab && packageTable === 'package_credit_additions';
  const contentIdField = isContentTab ? CONTENT_TABLE_ID_FIELD[contentTable] : null;
  const contentStatusField = isContentTab ? CONTENT_TABLE_STATUS_FIELD[contentTable] ?? null : null;

  useEffect(() => {
    setRows([]);
    setError(null);
    setLastFetchedAt(null);
  }, [activeTab, contentTable, packageTable]);

  const checkRlsContext = useCallback(async () => {
    setRlsProbeLoading(true);
    setRlsProbeError(null);

    try {
      const [issuerResult, accessResult] = await Promise.all([
        supabase.rpc('is_allowed_token_issuer'),
        selectedClient?.clientId
          ? supabase.rpc('has_client_access', { target_client_id: selectedClient.clientId })
          : Promise.resolve({ data: null, error: null }),
      ]);

      if (issuerResult.error) throw issuerResult.error;
      if (accessResult.error) throw accessResult.error;

      setAllowedIssuer(Boolean(issuerResult.data));
      setSelectedClientAccess(
        selectedClient?.clientId ? Boolean((accessResult as { data: boolean | null }).data) : null,
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setAllowedIssuer(null);
      setSelectedClientAccess(null);
      setRlsProbeError(message);
    } finally {
      setRlsProbeLoading(false);
    }
  }, [selectedClient?.clientId]);

  useEffect(() => {
    void checkRlsContext();
  }, [checkRlsContext]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let query = supabase.from(tableName).select('*');

      const normalizedClientId = clientIdFilter.trim();
      if (normalizedClientId) {
        query = query.eq('clientId', normalizedClientId);
      }

      if (isFinancialProfilesTab || isAssessmentSessionsTab || isAssessmentResultsTab) {
        const normalizedPatientId = patientIdFilter.trim();
        if (normalizedPatientId) {
          query = query.eq('patientId', normalizedPatientId);
        }
      }

      if (isAssessmentSessionsTab || isAssessmentResultsTab) {
        const normalizedStatus = statusValueFilter.trim();
        if (normalizedStatus) {
          query = query.eq('status', normalizedStatus);
        }
      }

      if (isAssessmentResultsTab) {
        const normalizedSessionId = sessionIdFilter.trim();
        if (normalizedSessionId) {
          query = query.eq('sessionId', normalizedSessionId);
        }

        const normalizedTestKey = testKeyFilter.trim();
        if (normalizedTestKey) {
          query = query.eq('testKey', normalizedTestKey);
        }
      }

      if (isPackagesTab) {
        const normalizedPackageId = packageIdFilter.trim();
        if (normalizedPackageId) {
          query = query.eq('packageId', normalizedPackageId);
        }

        if (isPackagesMainTable) {
          const normalizedFinancialProfileId = financialProfileIdFilter.trim();
          if (normalizedFinancialProfileId) {
            query = query.eq('financialProfileId', normalizedFinancialProfileId);
          }
        }

        if (isPackagesUsageTable) {
          const normalizedAppointmentId = appointmentIdFilter.trim();
          if (normalizedAppointmentId) {
            query = query.eq('appointmentId', normalizedAppointmentId);
          }
        }

        if (isPackagesCreditsTable) {
          const normalizedInvoiceId = invoiceIdFilter.trim();
          if (normalizedInvoiceId) {
            query = query.eq('invoiceId', normalizedInvoiceId);
          }
        }
      }

      if (isContentTab) {
        const normalizedRecordId = contentRecordIdFilter.trim();
        if (normalizedRecordId && contentIdField) {
          query = query.eq(contentIdField, normalizedRecordId);
        }

        const normalizedStatus = contentStatusFilter.trim();
        if (normalizedStatus && contentStatusField) {
          query = query.eq(contentStatusField, normalizedStatus);
        }
      }

      const { data, error: queryError } = await query
        .order(orderField, { ascending: false })
        .limit(limitValue);

      if (queryError) {
        throw queryError;
      }

      setRows((data ?? []) as RLSRow[]);
      setLastFetchedAt(new Date().toISOString());
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setRows([]);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [
    appointmentIdFilter,
    clientIdFilter,
    contentIdField,
    contentRecordIdFilter,
    contentStatusField,
    contentStatusFilter,
    financialProfileIdFilter,
    invoiceIdFilter,
    isAssessmentResultsTab,
    isAssessmentSessionsTab,
    isFinancialProfilesTab,
    isPackagesCreditsTable,
    isPackagesMainTable,
    isPackagesTab,
    isPackagesUsageTable,
    limitValue,
    orderField,
    packageIdFilter,
    patientIdFilter,
    sessionIdFilter,
    statusValueFilter,
    tableName,
    testKeyFilter,
  ]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await fetchRows();
  };

  const formatCellValue = (value: unknown): string => {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    try {
      return JSON.stringify(value);
    } catch {
      return '[unserializable]';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-3">
            <ShieldCheck className="h-6 w-6 text-green-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Teste de RLS</h1>
              <p className="text-sm text-gray-600">
                Consulta técnica para validar isolamento por usuário em tabelas protegidas por RLS.
              </p>
            </div>
          </div>

          <div className="mb-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setActiveTab('pilot')}
              className={`rounded-lg px-3 py-2 text-sm font-medium transition ${
                activeTab === 'pilot'
                  ? 'bg-blue-600 text-white'
                  : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              RLS Pilot
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('financialProfiles')}
              className={`rounded-lg px-3 py-2 text-sm font-medium transition ${
                activeTab === 'financialProfiles'
                  ? 'bg-blue-600 text-white'
                  : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Financial Profiles
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('packages')}
              className={`rounded-lg px-3 py-2 text-sm font-medium transition ${
                activeTab === 'packages'
                  ? 'bg-blue-600 text-white'
                  : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Packages
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('clientReportSettings')}
              className={`rounded-lg px-3 py-2 text-sm font-medium transition ${
                activeTab === 'clientReportSettings'
                  ? 'bg-blue-600 text-white'
                  : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Client Report Settings
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('assessmentSessionsV2')}
              className={`rounded-lg px-3 py-2 text-sm font-medium transition ${
                activeTab === 'assessmentSessionsV2'
                  ? 'bg-blue-600 text-white'
                  : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Assessment Sessions V2
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('assessmentResultsV2')}
              className={`rounded-lg px-3 py-2 text-sm font-medium transition ${
                activeTab === 'assessmentResultsV2'
                  ? 'bg-blue-600 text-white'
                  : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Assessment Results V2
            </button>
          </div>

          <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="font-medium text-gray-700">Usuário (Supabase table)</p>
              <p className="text-gray-600">userId: {user?.userId ?? '-'}</p>
              <p className="text-gray-600">firebase_uid: {user?.firebase_uid ?? '-'}</p>
              <p className="text-gray-600">email: {user?.userEmail ?? '-'}</p>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="font-medium text-gray-700">Contexto Atual</p>
              <p className="text-gray-600">selectedClient.clientId: {selectedClient?.clientId ?? '(não selecionado)'}</p>
              <p className="text-gray-600">selectedClient.userRole: {selectedClient?.userRole ?? '-'}</p>
              <p className="text-gray-600">userGlobalRole: {user?.userGlobalRole ?? '-'}</p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-900">
              <p className="font-medium">Racional do RLS nesta V2</p>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>Todas as tabelas novas usam `clientId` como fronteira do tenant.</li>
                <li>O RLS está com `ENABLE` e `FORCE ROW LEVEL SECURITY`.</li>
                <li>As policies exigem `is_allowed_token_issuer()` e `has_client_access(clientId)`.</li>
                <li>As consultas desta tela usam o cliente autenticado do navegador, sem service role.</li>
              </ul>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
              <div className="mb-2 flex items-center justify-between">
                <p className="font-medium text-gray-900">Sondas de RLS</p>
                <button
                  type="button"
                  onClick={() => void checkRlsContext()}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${rlsProbeLoading ? 'animate-spin' : ''}`} />
                  Validar
                </button>
              </div>
              {rlsProbeError ? (
                <div className="rounded-md border border-red-200 bg-red-50 p-2 text-red-700">{rlsProbeError}</div>
              ) : (
                <div className="space-y-1">
                  <p>
                    `is_allowed_token_issuer()`:{' '}
                    <strong>{rlsProbeLoading ? 'validando...' : allowedIssuer == null ? '—' : String(allowedIssuer)}</strong>
                  </p>
                  <p>
                    `has_client_access(selectedClient.clientId)`:{' '}
                    <strong>
                      {rlsProbeLoading ? 'validando...' : selectedClientAccess == null ? '—' : String(selectedClientAccess)}
                    </strong>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
            Tabela ativa: <code>{tableName}</code>
          </div>

          <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-6">
            <label className="md:col-span-2">
              <span className="mb-1 flex items-center gap-2 text-sm font-medium text-gray-700">
                <Filter className="h-4 w-4" />
                Filtro por clientId
              </span>
              <input
                type="text"
                value={clientIdFilter}
                onChange={(e) => setClientIdFilter(e.target.value)}
                placeholder="UUID do tenant (opcional)"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </label>

            {isPackagesTab && (
              <label>
                <span className="mb-1 block text-sm font-medium text-gray-700">Tabela de pacotes</span>
                <select
                  value={packageTable}
                  onChange={(e) => setPackageTable(e.target.value as PackageTable)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  {PACKAGE_TABLE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {isContentTab && (
              <label>
                <span className="mb-1 block text-sm font-medium text-gray-700">Tabela de conteúdo</span>
                <select
                  value={contentTable}
                  onChange={(e) => setContentTable(e.target.value as ContentTable)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  {CONTENT_TABLE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {isFinancialProfilesTab && (
              <label>
                <span className="mb-1 block text-sm font-medium text-gray-700">Filtro por patientId</span>
                <input
                  type="text"
                  value={patientIdFilter}
                  onChange={(e) => setPatientIdFilter(e.target.value)}
                  placeholder="UUID do paciente (opcional)"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </label>
            )}

            {(isAssessmentSessionsTab || isAssessmentResultsTab) && (
              <label>
                <span className="mb-1 block text-sm font-medium text-gray-700">Filtro por patientId</span>
                <input
                  type="text"
                  value={patientIdFilter}
                  onChange={(e) => setPatientIdFilter(e.target.value)}
                  placeholder="UUID do paciente (opcional)"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </label>
            )}

            {(isAssessmentSessionsTab || isAssessmentResultsTab) && (
              <label>
                <span className="mb-1 block text-sm font-medium text-gray-700">Filtro por status</span>
                <select
                  value={statusValueFilter}
                  onChange={(e) => setStatusValueFilter(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  <option value="">Todos</option>
                  <option value="draft">draft</option>
                  <option value="completed">completed</option>
                  <option value="cancelled">cancelled</option>
                </select>
              </label>
            )}

            {isAssessmentResultsTab && (
              <label>
                <span className="mb-1 block text-sm font-medium text-gray-700">Filtro por sessionId</span>
                <input
                  type="text"
                  value={sessionIdFilter}
                  onChange={(e) => setSessionIdFilter(e.target.value)}
                  placeholder="UUID da sessão (opcional)"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </label>
            )}

            {isAssessmentResultsTab && (
              <label>
                <span className="mb-1 block text-sm font-medium text-gray-700">Filtro por testKey</span>
                <input
                  type="text"
                  value={testKeyFilter}
                  onChange={(e) => setTestKeyFilter(e.target.value)}
                  placeholder="CAT, mMRC, TC6..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </label>
            )}

            {isPackagesTab && (
              <label>
                <span className="mb-1 block text-sm font-medium text-gray-700">Filtro por packageId</span>
                <input
                  type="text"
                  value={packageIdFilter}
                  onChange={(e) => setPackageIdFilter(e.target.value)}
                  placeholder="UUID do pacote (opcional)"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </label>
            )}

            {isContentTab && contentIdField && (
              <label>
                <span className="mb-1 block text-sm font-medium text-gray-700">Filtro por {contentIdField}</span>
                <input
                  type="text"
                  value={contentRecordIdFilter}
                  onChange={(e) => setContentRecordIdFilter(e.target.value)}
                  placeholder={`UUID de ${contentIdField} (opcional)`}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </label>
            )}

            {isContentTab && contentStatusField && (
              <label>
                <span className="mb-1 block text-sm font-medium text-gray-700">Filtro por {contentStatusField}</span>
                <input
                  type="text"
                  value={contentStatusFilter}
                  onChange={(e) => setContentStatusFilter(e.target.value)}
                  placeholder={`Ex.: active / inactive / new`}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </label>
            )}

            {isPackagesMainTable && (
              <label>
                <span className="mb-1 block text-sm font-medium text-gray-700">Filtro por financialProfileId</span>
                <input
                  type="text"
                  value={financialProfileIdFilter}
                  onChange={(e) => setFinancialProfileIdFilter(e.target.value)}
                  placeholder="UUID do perfil financeiro (opcional)"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </label>
            )}

            {isPackagesUsageTable && (
              <label>
                <span className="mb-1 block text-sm font-medium text-gray-700">Filtro por appointmentId</span>
                <input
                  type="text"
                  value={appointmentIdFilter}
                  onChange={(e) => setAppointmentIdFilter(e.target.value)}
                  placeholder="UUID do agendamento (opcional)"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </label>
            )}

            {isPackagesCreditsTable && (
              <label>
                <span className="mb-1 block text-sm font-medium text-gray-700">Filtro por invoiceId</span>
                <input
                  type="text"
                  value={invoiceIdFilter}
                  onChange={(e) => setInvoiceIdFilter(e.target.value)}
                  placeholder="UUID da fatura (opcional)"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </label>
            )}

            <label>
              <span className="mb-1 block text-sm font-medium text-gray-700">Limite</span>
              <select
                value={limitValue}
                onChange={(e) => setLimitValue(Number(e.target.value))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                {LIMIT_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>

            <div className="flex items-end gap-2">
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-400"
              >
                <Database className="h-4 w-4" />
                Buscar
              </button>
              <button
                type="button"
                onClick={() => void fetchRows()}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-70"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Atualizar
              </button>
            </div>
          </form>

          <div className="mt-4 text-sm text-gray-600">
            <p>
              Query aplicada: <code>from('{tableName}').select('*').order('{orderField}', desc).limit({limitValue})</code>
            </p>
            {isFinancialProfilesTab && patientIdFilter.trim() && (
              <p>
                Filtro adicional: <code>eq('patientId', '{patientIdFilter.trim()}')</code>
              </p>
            )}
            {(isAssessmentSessionsTab || isAssessmentResultsTab) && patientIdFilter.trim() && (
              <p>
                Filtro adicional: <code>eq('patientId', '{patientIdFilter.trim()}')</code>
              </p>
            )}
            {(isAssessmentSessionsTab || isAssessmentResultsTab) && statusValueFilter.trim() && (
              <p>
                Filtro adicional: <code>eq('status', '{statusValueFilter.trim()}')</code>
              </p>
            )}
            {isAssessmentResultsTab && sessionIdFilter.trim() && (
              <p>
                Filtro adicional: <code>eq('sessionId', '{sessionIdFilter.trim()}')</code>
              </p>
            )}
            {isAssessmentResultsTab && testKeyFilter.trim() && (
              <p>
                Filtro adicional: <code>eq('testKey', '{testKeyFilter.trim()}')</code>
              </p>
            )}
            {isPackagesTab && packageIdFilter.trim() && (
              <p>
                Filtro adicional: <code>eq('packageId', '{packageIdFilter.trim()}')</code>
              </p>
            )}
            {isPackagesMainTable && financialProfileIdFilter.trim() && (
              <p>
                Filtro adicional: <code>eq('financialProfileId', '{financialProfileIdFilter.trim()}')</code>
              </p>
            )}
            {isPackagesUsageTable && appointmentIdFilter.trim() && (
              <p>
                Filtro adicional: <code>eq('appointmentId', '{appointmentIdFilter.trim()}')</code>
              </p>
            )}
            {isPackagesCreditsTable && invoiceIdFilter.trim() && (
              <p>
                Filtro adicional: <code>eq('invoiceId', '{invoiceIdFilter.trim()}')</code>
              </p>
            )}
            {isContentTab && contentIdField && contentRecordIdFilter.trim() && (
              <p>
                Filtro adicional: <code>eq('{contentIdField}', '{contentRecordIdFilter.trim()}')</code>
              </p>
            )}
            {isContentTab && contentStatusField && contentStatusFilter.trim() && (
              <p>
                Filtro adicional: <code>eq('{contentStatusField}', '{contentStatusFilter.trim()}')</code>
              </p>
            )}
            {isContentTab && (
              <p>
                Dica: nesta aba validamos acesso autenticado direto nas tabelas (não RPC pública).
              </p>
            )}
            {lastFetchedAt && <p>Última atualização: {new Date(lastFetchedAt).toLocaleString('pt-BR')}</p>}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Resultado ({rows.length} linhas)</h2>

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <strong>Erro na consulta:</strong> {error}
            </div>
          )}

          {!error && !loading && rows.length === 0 && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
              Sem dados para os filtros informados (ou sem permissão RLS para visualizar linhas).
            </div>
          )}

          {rows.length > 0 && (
            <div className="overflow-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {columns.map((column) => (
                      <th
                        key={column}
                        className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600"
                      >
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {rows.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      {columns.map((column) => (
                        <td key={`${index}-${column}`} className="max-w-[320px] whitespace-pre-wrap break-words px-4 py-3 text-sm text-gray-700">
                          {formatCellValue(row[column])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
